import React from 'react';

/**
 * AmbientBackground - Renders aurora gradients and grid overlay
 * @param {string} variant - 'landing' | 'dashboard' (dashboard has reduced opacity)
 * @param {React.ReactNode} children - Content to render above background
 */
const AmbientBackground = ({ variant = 'landing', children }) => {
    const isDashboard = variant === 'dashboard';

    // Keyframes for aurora animation - must be injected as a style tag
    // Using only translate (no scale) to prevent layout recalculations
    const keyframes = `
        @keyframes aurora-drift {
            0%, 100% {
                transform: translate3d(0, 0, 0);
            }
            25% {
                transform: translate3d(2%, 1%, 0);
            }
            50% {
                transform: translate3d(0, 2%, 0);
            }
            75% {
                transform: translate3d(-2%, 1%, 0);
            }
        }
    `;

    const styles = {
        container: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 0,
            overflow: 'hidden',
            // Use contain to prevent layout recalculations from affecting the rest of the page
            contain: 'strict',
        },
        aurora: {
            position: 'absolute',
            filter: 'blur(100px)',
            opacity: isDashboard ? 0.2 : 0.4,
            // GPU acceleration - use will-change sparingly and backface-visibility for compositing
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
        },
        aurora1: {
            top: '-20%',
            left: '10%',
            width: '60%',
            height: '50%',
            background: 'radial-gradient(ellipse, var(--indigo-500) 0%, transparent 70%)',
            animation: 'aurora-drift 20s ease-in-out infinite',
        },
        aurora2: {
            top: '30%',
            right: '-10%',
            width: '50%',
            height: '40%',
            background: 'radial-gradient(ellipse, var(--cyan-400) 0%, transparent 70%)',
            animation: 'aurora-drift 25s ease-in-out infinite',
            animationDelay: '-7s',
        },
        aurora3: {
            bottom: '-10%',
            left: '30%',
            width: '40%',
            height: '35%',
            background: 'radial-gradient(ellipse, var(--indigo-600) 0%, transparent 70%)',
            animation: 'aurora-drift 22s ease-in-out infinite',
            animationDelay: '-14s',
        },
        gridOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `
                linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            maskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 40%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 50%, black 40%, transparent 100%)',
        },
    };

    return (
        <>
            <style>{keyframes}</style>
            <div style={styles.container}>
                <div style={{ ...styles.aurora, ...styles.aurora1 }} />
                <div style={{ ...styles.aurora, ...styles.aurora2 }} />
                {!isDashboard && <div style={{ ...styles.aurora, ...styles.aurora3 }} />}
                <div style={styles.gridOverlay} />
            </div>
            {children}
        </>
    );
};

export default AmbientBackground;
